import { RegistrationItem } from 'src/types';

export type SyncMode = 'replace' | 'append';

export interface SyncBookingsResult {
  next: RegistrationItem[];
  hasChanges: boolean;
}

const hasItemChanged = (a: RegistrationItem, b: RegistrationItem): boolean =>
  a.lastModifiedDateTime !== b.lastModifiedDateTime;

const collectionsEqual = (
  current: RegistrationItem[],
  incoming: RegistrationItem[]
): boolean => {
  if (current.length !== incoming.length) return false;

  const currentById = new Map(
    current.map((item) => [item.registrationId, item])
  );

  for (const item of incoming) {
    const existing = currentById.get(item.registrationId);
    if (!existing || hasItemChanged(existing, item)) {
      return false;
    }
  }

  return true;
};

/**
 * Syncs incoming API bookings into the current list.
 * Returns the same array reference when nothing changed (avoids unnecessary re-renders).
 */
export const syncBookings = (
  current: RegistrationItem[],
  incoming: RegistrationItem[],
  { mode }: { mode: SyncMode }
): SyncBookingsResult => {
  if (mode === 'append') {
    if (incoming.length === 0) {
      return { next: current, hasChanges: false };
    }

    const byId = new Map(current.map((item) => [item.registrationId, item]));
    let hasChanges = false;

    for (const item of incoming) {
      const existing = byId.get(item.registrationId);
      if (!existing) {
        byId.set(item.registrationId, item);
        hasChanges = true;
      } else if (hasItemChanged(existing, item)) {
        byId.set(item.registrationId, item);
        hasChanges = true;
      }
    }

    if (!hasChanges) {
      return { next: current, hasChanges: false };
    }

    return { next: Array.from(byId.values()), hasChanges: true };
  }

  if (collectionsEqual(current, incoming)) {
    return { next: current, hasChanges: false };
  }

  return { next: incoming, hasChanges: true };
};

export const PAGE_SIZE = 100;

/**
 * Replaces page-1 data in place while keeping items beyond the first page.
 * Used during silent refresh when the full list is already loaded.
 */
export const syncPageOneInPlace = (
  current: RegistrationItem[],
  incoming: RegistrationItem[]
): SyncBookingsResult => {
  const trailing = current.slice(PAGE_SIZE);
  const incomingIds = new Set(incoming.map((item) => item.registrationId));
  const trailingFiltered = trailing.filter(
    (item) => !incomingIds.has(item.registrationId)
  );

  const next = [...incoming, ...trailingFiltered];
  return syncBookings(current, next, { mode: 'replace' });
};
